"use client";

/**
 * AppSidebar — Wave 34 Claude Design integration.
 * Design class library (.sidebar, .sidebar-item, .sidebar-workspace, etc.) +
 * 23-item nav + workspace switcher chip + active-state highlight.
 * Tüm tRPC + RLS + route data flow korunuyor — sadece visual layer.
 */
import * as React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

interface NavItem { kind: "item"; href: string; label: string; icon: string; badge?: number; badgeTone?: "danger" | "brand" | "neutral" }
interface NavGroup { kind: "group"; label: string }
type NavEntry = NavItem | NavGroup;

// Wave 35 IA cleanup (CDO finding):
// - "Eylem" → "Sistem" (Audit + Scrape Health = operational telemetry, not action)
// - New top "Çalışma" group consolidates Aksiyon Kutusu + Sor + Yatırım 2×2
// - Dağıtım grubu kaldırıldı, içerikleri Görünürlük altına taşındı (compact)
// - 23 leaf (was 26), 5 groups (was 6)
const NAV: NavEntry[] = [
  { kind: "item", href: "/dashboard", label: "Ana Pano", icon: "dashboard" },
  { kind: "group", label: "Çalışma" },
  { kind: "item", href: "/inbox", label: "Aksiyon Kutusu", icon: "inbox", badge: 32, badgeTone: "danger" },
  { kind: "item", href: "/ask", label: "Sor", icon: "chat" },
  { kind: "item", href: "/investment-allocator", label: "Yatırım 2×2", icon: "matrix" },
  { kind: "group", label: "Görünürlük" },
  { kind: "item", href: "/share-of-shelf", label: "Share of Shelf", icon: "shelf" },
  { kind: "item", href: "/keywords", label: "Anahtar Kelime", icon: "keyword" },
  { kind: "item", href: "/sponsored-sov", label: "Sponsorlu SoV", icon: "sponsored" },
  { kind: "item", href: "/slots", label: "Slot İzleyici", icon: "slot" },
  { kind: "item", href: "/buybox-tracker", label: "BuyBox Pay", icon: "buybox" },
  { kind: "item", href: "/seller-map", label: "Seller Haritası", icon: "seller" },
  { kind: "group", label: "Fiyat & Promo" },
  { kind: "item", href: "/price-tracker", label: "Fiyat Takip", icon: "price" },
  { kind: "item", href: "/promo-watch", label: "Promosyon", icon: "promo" },
  { kind: "item", href: "/campaign-pulse", label: "Kampanya Nabzı", icon: "campaign" },
  { kind: "group", label: "İçerik & Rekabet" },
  { kind: "item", href: "/content-quality", label: "İçerik Kalitesi", icon: "content" },
  { kind: "item", href: "/image-compliance", label: "Görsel Uyum", icon: "image" },
  { kind: "item", href: "/copy-optimizer", label: "Copy Optimizer", icon: "copy" },
  { kind: "item", href: "/image-gaps", label: "Görsel Eksik", icon: "visual" },
  { kind: "item", href: "/competitor-scorecard", label: "Rakip Karne", icon: "competitor" },
  { kind: "item", href: "/voice-of-shopper", label: "Yorum Sesi", icon: "reviews" },
  { kind: "item", href: "/availability", label: "Stok Görünürlüğü", icon: "buybox" },
  { kind: "item", href: "/forecasting", label: "Tahmin", icon: "forecast" },
  { kind: "item", href: "/velocity", label: "Satış Hızı", icon: "velocity" },
  { kind: "group", label: "Sistem" },
  { kind: "item", href: "/audit", label: "Audit", icon: "audit" },
  { kind: "item", href: "/scrape-health", label: "Scrape Health", icon: "health" },
];

const ICONS: Record<string, React.ReactNode> = {
  dashboard: <svg viewBox="0 0 16 16" fill="none"><rect x="1.5" y="1.5" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="8.5" y="1.5" width="6" height="3" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="8.5" y="5.5" width="6" height="9" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="1.5" y="8.5" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/></svg>,
  inbox: <svg viewBox="0 0 16 16" fill="none"><path d="M1.5 8h3l1.5 3h4l1.5-3h3v4.5a2 2 0 01-2 2h-9a2 2 0 01-2-2V8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M2.5 8L4 2h8l1.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  chat: <svg viewBox="0 0 16 16" fill="none"><path d="M3 12.5L1.5 14.5V3a1.5 1.5 0 011.5-1.5h10A1.5 1.5 0 0114.5 3v8a1.5 1.5 0 01-1.5 1.5H3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><circle cx="5.5" cy="7" r="0.75" fill="currentColor"/><circle cx="8" cy="7" r="0.75" fill="currentColor"/><circle cx="10.5" cy="7" r="0.75" fill="currentColor"/></svg>,
  shelf: <svg viewBox="0 0 16 16" fill="none"><path d="M1.5 14.5h13M3 14.5V6.5h3v8M7 14.5V3.5h3v11M11 14.5V8.5h3v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  keyword: <svg viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5"/><path d="M10.5 10.5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  sponsored: <svg viewBox="0 0 16 16" fill="none"><path d="M2 10l4-4 3 3 5-5M14 5h-2.5M14 5v2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  slot: <svg viewBox="0 0 16 16" fill="none"><rect x="1.5" y="1.5" width="13" height="13" rx="1" stroke="currentColor" strokeWidth="1.5"/><path d="M1.5 6h13M6 1.5v13" stroke="currentColor" strokeWidth="1.5"/></svg>,
  buybox: <svg viewBox="0 0 16 16" fill="none"><path d="M2 5l6-3 6 3v6l-6 3-6-3V5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M2 5l6 3 6-3M8 8v6" stroke="currentColor" strokeWidth="1.5"/></svg>,
  price: <svg viewBox="0 0 16 16" fill="none"><path d="M2 5h12M2 11h12M5 2v12M11 2v12" stroke="currentColor" strokeWidth="1.5"/></svg>,
  promo: <svg viewBox="0 0 16 16" fill="none"><path d="M8 2l1.5 4.5L14 8l-4.5 1.5L8 14l-1.5-4.5L2 8l4.5-1.5L8 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  campaign: <svg viewBox="0 0 16 16" fill="none"><path d="M2 8l10-5v10L2 8zM2 8v2h2M5 13v-1" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"/></svg>,
  content: <svg viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="12" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M5 6h6M5 9h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  image: <svg viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="12" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><circle cx="6" cy="6" r="1.25" fill="currentColor"/><path d="M2 11l3.5-3.5L9 11l2-2 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  copy: <svg viewBox="0 0 16 16" fill="none"><rect x="2" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M5 5V2.5A1 1 0 016 1.5h7A1 1 0 0114 2.5v9a1 1 0 01-1 1h-2" stroke="currentColor" strokeWidth="1.5"/></svg>,
  visual: <svg viewBox="0 0 16 16" fill="none"><rect x="1.5" y="1.5" width="13" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2"/><path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  velocity: <svg viewBox="0 0 16 16" fill="none"><path d="M2 13l3-3 3 3 6-6M14 7V4h-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  competitor: <svg viewBox="0 0 16 16" fill="none"><circle cx="5" cy="6" r="2" stroke="currentColor" strokeWidth="1.5"/><circle cx="11" cy="6" r="2" stroke="currentColor" strokeWidth="1.5"/><path d="M2 14c0-2 1.5-4 3-4M14 14c0-2-1.5-4-3-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  reviews: <svg viewBox="0 0 16 16" fill="none"><path d="M8 1.5L9.6 6h4.4l-3.5 2.6L11.7 13 8 10.2 4.3 13l1.2-4.4L2 6h4.4L8 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  forecast: <svg viewBox="0 0 16 16" fill="none"><path d="M2 12c2 0 2-6 4-6s2 6 4 6 2-6 4-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  product: <svg viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="12" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M2 6h12M6 2v12" stroke="currentColor" strokeWidth="1.5"/></svg>,
  seller: <svg viewBox="0 0 16 16" fill="none"><path d="M2 6l1.5-4h9L14 6M2 6v7a1 1 0 001 1h10a1 1 0 001-1V6M2 6h12M6 10h4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"/></svg>,
  distribution: <svg viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/><path d="M8 1.5v13M1.5 8h13" stroke="currentColor" strokeWidth="1.5"/></svg>,
  matrix: <svg viewBox="0 0 16 16" fill="none"><rect x="1.5" y="1.5" width="13" height="13" rx="1" stroke="currentColor" strokeWidth="1.5"/><path d="M1.5 8h13M8 1.5v13" stroke="currentColor" strokeWidth="1.5"/></svg>,
  audit: <svg viewBox="0 0 16 16" fill="none"><path d="M3 2h7l3 3v9a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M5 9l2 2 4-4M10 2v3h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  health: <svg viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="2.25" stroke="currentColor" strokeWidth="1.5"/><path d="M8 1.5v2M8 12.5v2M14.5 8h-2M3.5 8h-2M12.6 3.4l-1.4 1.4M4.8 11.2l-1.4 1.4M12.6 12.6l-1.4-1.4M4.8 4.8L3.4 3.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
};

interface AppSidebarProps {
  workspaceName?: string;
  workspaceMeta?: string;
  workspaceLetter?: string;
  workspaceColor?: string;
  userInitials?: string;
  userEmail?: string;
}

export function AppSidebar({ workspaceName = "Demo FMCG Workspace", workspaceMeta = "owner · TR", workspaceLetter = "D", workspaceColor, userInitials = "S", userEmail = "selin@demo.commerco.com" }: AppSidebarProps): React.JSX.Element {
  const pathname = usePathname() ?? "";

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <div className="sidebar-monogram" aria-hidden>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 8.5a8 8 0 1 0 0 7" />
            <path d="M14 5 19 8.5l-3 5" />
          </svg>
        </div>
        <div className="sidebar-wordmark">Commerco</div>
      </div>

      <div className="sidebar-workspace" role="button" tabIndex={0}>
        <span className="sidebar-workspace-chip" style={workspaceColor ? { background: workspaceColor } : undefined}>{workspaceLetter}</span>
        <div className="sidebar-workspace-info">
          <div className="sidebar-workspace-name">{workspaceName}</div>
          <div className="sidebar-workspace-meta">{workspaceMeta}</div>
        </div>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      <button
        type="button"
        className="sidebar-search"
        onClick={() => document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true }))}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
          <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <span className="sidebar-search-text">Ara…</span>
        <span className="kbd">⌘K</span>
      </button>

      <nav className="sidebar-nav">
        {NAV.map((entry, idx) => {
          if (entry.kind === "group") {
            return <div key={`g-${idx}`} className="sidebar-group-label">{entry.label}</div>;
          }
          const active = pathname === entry.href || pathname.startsWith(entry.href + "/");
          return (
            <Link
              key={entry.href}
              href={entry.href}
              aria-current={active ? "page" : undefined}
              data-active={active ? "true" : undefined}
              className="sidebar-item"
            >
              <span className="sidebar-item-icon">{ICONS[entry.icon] ?? ICONS.dashboard}</span>
              <span className="sidebar-item-label">{entry.label}</span>
              {entry.badge !== undefined && (
                <span className="sidebar-item-badge" data-tone={entry.badgeTone ?? "danger"}>{entry.badge}</span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-avatar">{userInitials}</div>
        <div className="sidebar-user">
          <div className="sidebar-user-name">{userEmail.split("@")[0]}</div>
          <div className="sidebar-user-org">{userEmail}</div>
        </div>
      </div>
    </aside>
  );
}
